import { useEffect, useState } from "react";

const CHAPTERS = [
  { name: "Abstract", status: "done" },
  { name: "Chapter One — Introduction", status: "done" },
  { name: "Chapter Two — Literature Review", status: "done" },
  { name: "Chapter Three — Methodology", status: "done" },
  { name: "Chapter Four — Implementation", status: "editing" },
  { name: "Chapter Five — Conclusion", status: "pending" },
  { name: "References", status: "pending" },
];

const PROJECT_TITLE =
  "Design and Implementation of a Smart Campus Navigation System";

const StatusBadge = ({ status }) => {
  if (status === "done") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-emerald-200/60">
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
        Done
      </span>
    );
  }
  if (status === "editing") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200/60">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-hero-ping rounded-full bg-amber-400 opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-500" />
        </span>
        Editing
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-500 ring-1 ring-slate-200/80 hero-shimmer">
      Generate
    </span>
  );
};

const HeroOutlineCard = () => {
  const [typedTitle, setTypedTitle] = useState("");
  const [showChapters, setShowChapters] = useState(false);
  const [typingDone, setTypingDone] = useState(false);

  useEffect(() => {
    let index = 0;
    const timer = setInterval(() => {
      index += 1;
      setTypedTitle(PROJECT_TITLE.slice(0, index));
      if (index >= PROJECT_TITLE.length) {
        clearInterval(timer);
        setTypingDone(true);
        setTimeout(() => setShowChapters(true), 200);
      }
    }, 28);
    return () => clearInterval(timer);
  }, []);

  const doneCount = CHAPTERS.filter((c) => c.status === "done").length;
  const progress = Math.round((doneCount / CHAPTERS.length) * 100);

  return (
    <div className="relative animate-hero-fade-up [animation-delay:400ms]">
      {/* Glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-4 rounded-3xl bg-gradient-to-br from-[#0BBF64]/20 via-emerald-100/30 to-slate-200/40 blur-2xl animate-hero-glow"
      />

      {/* Floating badge */}
      <div className="absolute -top-3 right-4 z-10 animate-hero-float">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#0BBF64]/30 bg-white/95 px-3 py-1 text-xs font-medium text-[#0BBF64] shadow-soft backdrop-blur-sm">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-hero-ping rounded-full bg-[#0BBF64] opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#0BBF64]" />
          </span>
          AI-generated outline
        </span>
      </div>

      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white/90 shadow-lift backdrop-blur-sm">
        {/* Top accent */}
        <div className="h-1 w-full bg-gradient-to-r from-[#0BBF64] via-emerald-400 to-teal-300" />

        <div className="p-5 sm:p-6">
          {/* Header */}
          <div className="mb-4 flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                BSc Computer Science · Final Year
              </p>
              <h3 className="mt-1 text-sm font-semibold leading-snug text-slate-800">
                {typedTitle}
                {!typingDone && (
                  <span className="ml-0.5 inline-block h-4 w-0.5 animate-hero-cursor bg-[#0BBF64] align-middle" />
                )}
              </h3>
            </div>
            <div className="shrink-0 rounded-lg bg-slate-50 px-2.5 py-1.5 text-center ring-1 ring-slate-100">
              <p className="text-lg font-bold leading-none text-[#0BBF64]">{doneCount}</p>
              <p className="text-[9px] font-medium text-slate-400">of {CHAPTERS.length}</p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-5">
            <div className="mb-1.5 flex items-center justify-between text-[10px]">
              <span className="font-medium text-slate-500">Documentation progress</span>
              <span className="font-semibold text-[#0BBF64]">{progress}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full origin-left rounded-full bg-gradient-to-r from-[#0BBF64] to-emerald-400 animate-hero-progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Chapter list */}
          <ul className="space-y-1">
            {CHAPTERS.map((chapter, i) => (
              <li
                key={chapter.name}
                className={`group flex items-center justify-between rounded-lg px-2.5 py-2 transition-colors ${
                  chapter.status === "editing"
                    ? "bg-amber-50/80 ring-1 ring-amber-100"
                    : "hover:bg-slate-50"
                } ${
                  showChapters
                    ? "animate-hero-slide-in opacity-100"
                    : "opacity-0"
                }`}
                style={{ animationDelay: showChapters ? `${i * 80}ms` : "0ms" }}
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-[10px] font-bold ${
                      chapter.status === "done"
                        ? "bg-emerald-100 text-emerald-700"
                        : chapter.status === "editing"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <span
                    className={`truncate text-xs ${
                      chapter.status === "pending"
                        ? "text-slate-400"
                        : "font-medium text-slate-700"
                    }`}
                  >
                    {chapter.name}
                  </span>
                </div>
                <StatusBadge status={chapter.status} />
              </li>
            ))}
          </ul>

          {/* Footer action */}
          <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
            <p className="text-[10px] text-slate-400">
              Last saved <span className="font-medium text-slate-500">2 min ago</span>
            </p>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-slate-800"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" />
              </svg>
              Regenerate
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const HeroSection = () => {
  return (
    <section className="relative overflow-hidden bg-white">
      {/* Background */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(11,191,100,0.12),transparent)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
        <div className="absolute -top-24 right-0 h-72 w-72 rounded-full bg-emerald-100/40 blur-3xl animate-hero-orb" />
        <div className="absolute -bottom-32 -left-16 h-64 w-64 rounded-full bg-slate-100/80 blur-3xl animate-hero-orb [animation-delay:2s]" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 lg:px-8 py-16 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
          {/* Copy */}
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200/80 bg-white/80 px-3.5 py-1.5 text-xs font-medium text-slate-600 shadow-sm backdrop-blur-sm animate-hero-fade-up mb-6">
              <span className="flex h-1.5 w-1.5 rounded-full bg-[#0BBF64]" />
              Built for Nigerian students · Final year, MSc, PhD
            </span>

            <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] lg:leading-[1.15] font-extrabold tracking-tight text-slate-900 animate-hero-fade-up [animation-delay:100ms]">
              Finish your{" "}
              <span className="bg-gradient-to-r from-[#0BBF64] to-emerald-600 bg-clip-text text-transparent">
                project documentation
              </span>{" "}
              in days, not months.
            </h1>

            <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed animate-hero-fade-up [animation-delay:200ms]">
              Astudent uses AI trained on common Nigerian university formats to
              help you generate clean, supervisor-ready project documentation for
              undergraduate, MSc and PhD projects.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center animate-hero-fade-up [animation-delay:300ms]">
              <a
                href="/signup"
                className="group inline-flex items-center justify-center gap-2 rounded-xl bg-[#0BBF64] px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition hover:bg-[#0aa459] hover:shadow-emerald-500/30"
              >
                Sign up free
                <svg
                  className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
              <a
                href="/pricing"
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-6 py-3.5 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                See pricing
              </a>
            </div>

            <p className="mt-5 text-xs sm:text-sm text-slate-500 animate-hero-fade-up [animation-delay:400ms]">
              No card required to start. Explore topic ideas and drafts before
              you pay for full documentation.
            </p>
          </div>

          {/* Visual */}
          <HeroOutlineCard />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
